import { useEffect, useState, type ReactElement } from "react";
import {
  catalogueProductLabel,
  DOSE_INTERVAL_COPY,
  doseProblem,
  isOnceAWeek,
  MEDICATION_CATALOGUE_COPY,
  prescriptionLabel,
  type MedicationCatalogue,
  type MedicationCatalogueProduct,
  type PrescriptionOrder,
  WEEKLY_FREQUENCY,
} from "@care-by-ella/contracts";
import { useOptionalAuth } from "@/auth/AuthContext";
import { fieldDomId } from "@/lib/form-errors";
import {
  loadedPickerCatalogue,
  pickerMedicationCatalogue,
} from "@/lib/medication-catalogue-api";
import {
  catalogueInForce,
  deviationRequired,
  intervalOptions,
  linkedVersionId,
  productInForce,
  productOf,
} from "@/lib/medication-picker";

// TES-636 — the prescription's three fields, in ONE place.
//
// Background: the patient's starting dose was never set by a doctor. The
// button in PatientDetail sent the string "0.25mg" hard-coded, and the server
// had the same default in two more places — the number the patient read as
// her prescription was a constant in this codebase. Renewal and dose
// adjustment in turn had a lone free-text field for the dose, with no
// medication and no interval.
//
// The component exists so that there is only one way to prescribe. If the
// fields live in each view separately they drift apart, and then we are back
// in a state where "dose" means different things depending on which button
// the doctor pressed.
//
// TES-956 (TES-941):
// "Läkemedel, dos, frekvens och behandlingslängd ska vara strukturerade uppgifter"
// The fourth field, the treatment length ("Behandlingslängd"), is optional
// and is ordination text only — it never changes the 28-day cycle. The same
// component is now also the meeting's Recept box (`variant="draft"`), so the
// order is typed once, at the meeting, and every later surface starts from it.
//
// TES-1292 (TES-1267 §7) — and the medication is picked from the medication
// catalogue. A product's dose and interval become selects once a doctor has
// clinically approved its entry; before that they stay free text, as they
// were, and the picker says "ej kliniskt godkänd". A value outside an
// approved entry, or a medication outside the catalogue once any entry is
// approved, is "Annat (avvikande)" and needs the doctor's motivation. The
// catalogue reference and the motivation ride on the draft; `toOrder` sends
// them. Without a catalogue (not loaded, or failed) the medication, dose and
// length are the text boxes they always were.
//
// TES-1375 (Ö-419) — the interval is never free text: Ella 2.1 supports
// dosing once a week only, so it is a select over the weekly value
// (`intervalOptions`), with no "Annat (avvikande)". The server refuses any
// other interval on an order (`weeklyFrequencySchema`).
export type OrderDraft = {
  medication: string;
  dose: string;
  frequency: string;
  duration: string;
  // The catalogue version the doctor picked from. When absent, the order
  // links itself to the product its medication names when it is sent.
  medicationVersionId?: string;
  // The doctor's motivation for a deviation ("Annat (avvikande)").
  deviationReason?: string;
};

export const EMPTY_ORDER: OrderDraft = {
  medication: "",
  dose: "",
  frequency: "",
  duration: "",
};

/** The prefill shape every surface reads: absent parts become empty fields. */
export function orderDraftFrom(o: {
  medication?: string | null;
  dose?: string | null;
  frequency?: string | null;
  duration?: string | null;
  medicationVersionId?: string | null;
  deviationReason?: string | null;
}): OrderDraft {
  return {
    medication: o.medication ?? "",
    dose: o.dose ?? "",
    frequency: o.frequency ?? "",
    duration: o.duration ?? "",
    ...(o.medicationVersionId
      ? { medicationVersionId: o.medicationVersionId }
      : {}),
    ...(o.deviationReason ? { deviationReason: o.deviationReason } : {}),
  };
}

/** True when the doctor has typed anything at all. */
export function orderTouched(d: OrderDraft): boolean {
  return Boolean(
    d.medication.trim() ||
    d.dose.trim() ||
    d.frequency.trim() ||
    d.duration.trim(),
  );
}

/**
 * TES-1292 — does the draft need a motivation? Read against the catalogue
 * the pickers loaded; without one it never does.
 */
export function orderNeedsDeviationReason(
  d: OrderDraft,
  catalogue: MedicationCatalogue | null = loadedPickerCatalogue(),
): boolean {
  return deviationRequired(catalogue, d);
}

/**
 * TES-1292 — the catalogue part of the order as sent: the version the parts
 * were picked from, and the motivation only while the order deviates (one
 * left over from an earlier choice is not sent).
 */
export function orderCatalogueRef(
  d: OrderDraft,
  catalogue: MedicationCatalogue | null = loadedPickerCatalogue(),
): { medicationVersionId?: string; deviationReason?: string } {
  const versionId = linkedVersionId(catalogue, d);
  const reason = d.deviationReason?.trim() ?? "";
  return {
    ...(versionId ? { medicationVersionId: versionId } : {}),
    ...(reason && orderNeedsDeviationReason(d, catalogue)
      ? { deviationReason: reason }
      : {}),
  };
}

/**
 * The three required parts are filled in, the interval is once a week
 * (TES-1375), and a deviation is motivated (TES-1292). The server refuses
 * otherwise — so should the button.
 */
export function orderComplete(d: OrderDraft): boolean {
  return Boolean(
    d.medication.trim() &&
    d.dose.trim() &&
    isOnceAWeek(d.frequency) &&
    (!orderNeedsDeviationReason(d) || d.deviationReason?.trim()),
  );
}

type OrderFieldKey =
  | "medication"
  | "dose"
  | "frequency"
  | "duration"
  | "deviationReason";

export type OrderFieldErrors = Partial<Record<OrderFieldKey, string>>;

/** The labels the error summary names the order's fields by. */
export const ORDER_FIELD_LABELS: Record<OrderFieldKey, string> = {
  medication: "Läkemedel",
  dose: "Dos",
  // TES-1185 — Emelie's word for it (TES-1180 point 1).
  frequency: "Doseringsintervall",
  duration: "Behandlingslängd",
  deviationReason: MEDICATION_CATALOGUE_COPY.deviationReasonLabel,
};

/**
 * TES-1031 — what each of the order's fields says when the order cannot be
 * published. The dose's text comes from the contract (`doseProblem`), so admin
 * and the server name the same fault in the same words.
 */
export function orderFieldErrors(d: OrderDraft): OrderFieldErrors {
  const out: OrderFieldErrors = {};
  if (!d.medication.trim()) out.medication = "Ange läkemedel, t.ex. Wegovy.";
  const dose = doseProblem(d.dose);
  if (dose) out.dose = dose;
  if (!d.frequency.trim()) out.frequency = DOSE_INTERVAL_COPY.required;
  else if (!isOnceAWeek(d.frequency))
    out.frequency = DOSE_INTERVAL_COPY.notSupported(d.frequency.trim());
  if (orderNeedsDeviationReason(d) && !d.deviationReason?.trim())
    out.deviationReason = MEDICATION_CATALOGUE_COPY.deviationReasonRequired;
  return out;
}

/**
 * Trimmed prescription to send. Call only when `orderComplete` is true.
 * The length is always sent, blank included: a blank clears a stored length
 * (on a dose adjustment), where leaving it out would keep it.
 */
export function toOrder(d: OrderDraft): PrescriptionOrder {
  return {
    medication: d.medication.trim(),
    dose: d.dose.trim(),
    frequency: d.frequency.trim(),
    duration: d.duration.trim(),
    ...orderCatalogueRef(d),
  };
}

/** TES-1292 — the catalogue the pickers share; null while it loads or failed. */
export function useMedicationCatalogue(): MedicationCatalogue | null {
  const [catalogue, setCatalogue] = useState<MedicationCatalogue | null>(
    loadedPickerCatalogue,
  );
  useEffect(() => {
    let live = true;
    pickerMedicationCatalogue().then(
      (c) => {
        if (live) setCatalogue(c);
      },
      // Without a catalogue the fields stay free text, as before it existed.
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, []);
  return catalogue;
}

const OTHER = "__other__";

const inputClass = (invalid: boolean) =>
  `rounded-md border bg-background px-sm py-xs text-sm ${
    invalid ? "border-danger" : "border-surfaceMuted"
  }`;

export function PrescriptionOrderFields({
  idPrefix,
  value,
  onChange,
  disabled,
  variant = "publish",
  errors,
  formId,
}: {
  idPrefix: string;
  value: OrderDraft;
  onChange: (next: OrderDraft) => void;
  disabled?: boolean;
  // TES-1031 — the errors to show at each field, set once the doctor has
  // tried to publish (or the server refused a field). `formId` names the
  // form the fields' wrappers belong to, so the form can scroll to them.
  errors?: OrderFieldErrors;
  formId?: string;
  // "publish": the order the patient will read — all three parts required,
  // with her view previewed. "draft": the order proposed at the meeting — may
  // be half-written, and nothing is published from it.
  variant?: "publish" | "draft";
}) {
  const catalogue = useMedicationCatalogue();
  // TES-1400 (Ö-541) — "Annat (avvikande) i läkemedelskatalogen kräver
  // särskild behörighet och obligatorisk motivering." Without the
  // permission the deviating choices are not offered; the API refuses one
  // anyway (403 medication_deviation_not_permitted).
  const claims = useOptionalAuth()?.claims;
  const mayDeviate =
    claims?.permissions?.includes("deviating_medication") ?? false;
  const products = catalogue?.products ?? [];
  const product = productOf(catalogue, value);
  const inForce = productInForce(product);
  // "Annat" chosen but not yet typed: the select must not jump back.
  const [otherMedication, setOtherMedication] = useState(false);
  const [otherDose, setOtherDose] = useState(false);

  // The dose's format errors, from the contract's own validation — one rule,
  // not two that can drift apart. Shown live as soon as something is typed;
  // an EMPTY dose is reported only once the doctor tries to publish.
  const doseError = value.dose.trim() ? doseProblem(value.dose) : null;
  const shown: OrderFieldErrors = {
    ...(doseError ? { dose: doseError } : {}),
    ...errors,
  };
  const needsReason = deviationRequired(catalogue, value);

  const wrap = (
    key: OrderFieldKey,
    width: string,
    control: (inputId: string, message: string | undefined) => ReactElement,
  ) => {
    const inputId = `${idPrefix}-${key}`;
    const message = shown[key];
    return (
      <div
        id={fieldDomId(formId ?? idPrefix, key)}
        className={`flex flex-col gap-xs ${width}`}
      >
        <label className="text-xs text-textSecondary" htmlFor={inputId}>
          {ORDER_FIELD_LABELS[key]}
        </label>
        {control(inputId, message)}
        {message ? (
          <p id={`${inputId}-error`} className="text-xs text-danger">
            {message}
          </p>
        ) : null}
      </div>
    );
  };

  const textInput = (
    key: "medication" | "dose" | "frequency" | "duration",
    placeholder: string,
    list?: string,
  ) =>
    function TextControl(inputId: string, message: string | undefined) {
      return (
        <input
          id={inputId}
          value={value[key]}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, [key]: e.target.value })}
          placeholder={placeholder}
          list={list}
          aria-invalid={message ? true : undefined}
          aria-describedby={message ? `${inputId}-error` : undefined}
          className={inputClass(Boolean(message))}
        />
      );
    };

  // TES-1292 — a select over the entry's values with "Annat (avvikande)",
  // which turns the field back into free text.
  const pickedField = (
    key: "dose",
    options: readonly string[],
    other: boolean,
    setOther: (v: boolean) => void,
    placeholder: string,
  ) =>
    function PickedControl(inputId: string, message: string | undefined) {
      const current = value[key].trim();
      const outside = other || (current !== "" && !options.includes(current));
      return (
        <>
          <select
            id={inputId}
            value={outside ? OTHER : current}
            disabled={disabled}
            onChange={(e) => {
              const v = e.target.value;
              if (v === OTHER) {
                setOther(true);
                onChange({ ...value, [key]: "" });
              } else {
                setOther(false);
                onChange({ ...value, [key]: v });
              }
            }}
            aria-invalid={message ? true : undefined}
            aria-describedby={message ? `${inputId}-error` : undefined}
            className={inputClass(Boolean(message))}
          >
            <option value="">Välj</option>
            {options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
            {mayDeviate || outside ? (
              <option value={OTHER}>
                {MEDICATION_CATALOGUE_COPY.deviation}
              </option>
            ) : null}
          </select>
          {outside ? (
            <input
              id={`${inputId}-other`}
              aria-label={`${ORDER_FIELD_LABELS[key]}, ${MEDICATION_CATALOGUE_COPY.deviation}`}
              value={value[key]}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, [key]: e.target.value })}
              placeholder={placeholder}
              className={inputClass(Boolean(message))}
            />
          ) : null}
        </>
      );
    };

  const pickProduct = (id: string) => {
    if (id === "") {
      setOtherMedication(false);
      onChange({ ...value, medication: "", medicationVersionId: undefined });
      return;
    }
    if (id === OTHER) {
      setOtherMedication(true);
      onChange({ ...value, medication: "", medicationVersionId: undefined });
      return;
    }
    const p = products.find((x) => x.id === id);
    if (!p) return;
    setOtherMedication(false);
    setOtherDose(false);
    const keep = (v: string, allowed: readonly string[]) =>
      // A value the new entry does not offer is cleared once the entry is
      // in force; free text is kept while it is not.
      productInForce(p) && !allowed.includes(v.trim()) ? "" : v;
    onChange({
      ...value,
      medication: p.name,
      medicationVersionId: p.current.id,
      dose: keep(value.dose, p.current.strengths),
      frequency: keep(
        value.frequency,
        intervalOptions(p, "", WEEKLY_FREQUENCY),
      ),
    });
  };

  const medicationSelect = (inputId: string, message: string | undefined) => {
    const typedOther = !product && value.medication.trim() !== "";
    const selected = product
      ? product.id
      : otherMedication || typedOther
        ? OTHER
        : "";
    return (
      <>
        <select
          id={inputId}
          value={selected}
          disabled={disabled}
          onChange={(e) => pickProduct(e.target.value)}
          aria-invalid={message ? true : undefined}
          aria-describedby={message ? `${inputId}-error` : undefined}
          className={inputClass(Boolean(message))}
        >
          <option value="">Välj läkemedel</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {productOptionLabel(p)}
            </option>
          ))}
          {!catalogueInForce(catalogue) || mayDeviate || selected === OTHER ? (
            <option value={OTHER}>
              {catalogueInForce(catalogue)
                ? MEDICATION_CATALOGUE_COPY.otherMedication
                : MEDICATION_CATALOGUE_COPY.freeTextMedication}
            </option>
          ) : null}
        </select>
        {selected === OTHER ? (
          <input
            id={`${inputId}-other`}
            aria-label={`${ORDER_FIELD_LABELS.medication}, fritext`}
            value={value.medication}
            disabled={disabled}
            onChange={(e) =>
              onChange({
                ...value,
                medication: e.target.value,
                medicationVersionId: undefined,
              })
            }
            placeholder="Läkemedlets namn"
            className={inputClass(Boolean(message))}
          />
        ) : null}
      </>
    );
  };

  // TES-1375 — a fixed choice. A prefilled value that is not weekly (an
  // older order) shows as "Välj" with the reason, so the doctor chooses again.
  const intervals = intervalOptions(product, value.frequency, WEEKLY_FREQUENCY);
  const intervalSelect = (inputId: string, message: string | undefined) => {
    const current = value.frequency.trim();
    return (
      <select
        id={inputId}
        value={intervals.includes(current) ? current : ""}
        disabled={disabled}
        onChange={(e) => onChange({ ...value, frequency: e.target.value })}
        aria-invalid={message ? true : undefined}
        aria-describedby={`${idPrefix}-frequency-hint${message ? ` ${inputId}-error` : ""}`}
        className={inputClass(Boolean(message))}
      >
        <option value="">Välj</option>
        {intervals.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  };

  const strengthsList = `${idPrefix}-catalogue-strengths`;

  return (
    <div className="flex flex-col gap-xs">
      {/* TES-1031 — each error sits under its own field (TES-702's dose
          format error included), so the fields align at the top. */}
      <div className="flex flex-wrap items-start gap-sm">
        {products.length > 0
          ? wrap("medication", "w-56", medicationSelect)
          : wrap("medication", "w-48", textInput("medication", "t.ex. Wegovy"))}
        {inForce && product
          ? wrap(
              "dose",
              "w-40",
              pickedField(
                "dose",
                product.current.strengths,
                otherDose,
                setOtherDose,
                "t.ex. 0,25 mg",
              ),
            )
          : wrap(
              "dose",
              "w-40",
              textInput(
                "dose",
                "t.ex. 0,25 mg",
                product ? strengthsList : undefined,
              ),
            )}
        {wrap("frequency", "w-48", intervalSelect)}
        {wrap("duration", "w-36", textInput("duration", "t.ex. i 4 veckor"))}
      </div>
      <p
        id={`${idPrefix}-frequency-hint`}
        className="text-xs text-textSecondary"
        data-testid="order-weekly-only"
      >
        {DOSE_INTERVAL_COPY.hint}
      </p>
      {/* Suggestions only, while the entry is not approved: the field stays
          free text. */}
      {product && !inForce ? (
        <>
          <datalist id={strengthsList}>
            {product.current.strengths.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
          <p
            className="text-xs text-textSecondary"
            data-testid="order-catalogue-not-approved"
          >
            {catalogueProductLabel(product)}:{" "}
            {MEDICATION_CATALOGUE_COPY.notApproved}. Dosen anges som fritext.
          </p>
        </>
      ) : null}
      {needsReason && !mayDeviate ? (
        <p
          className="text-xs text-danger"
          role="alert"
          data-testid="order-deviation-not-permitted"
        >
          {MEDICATION_CATALOGUE_COPY.deviationNotPermitted}
        </p>
      ) : null}
      {needsReason
        ? wrap("deviationReason", "w-full", (inputId, message) => (
            <textarea
              id={inputId}
              value={value.deviationReason ?? ""}
              disabled={disabled}
              rows={2}
              maxLength={1000}
              onChange={(e) =>
                onChange({ ...value, deviationReason: e.target.value })
              }
              aria-invalid={message ? true : undefined}
              aria-describedby={message ? `${inputId}-error` : undefined}
              className={inputClass(Boolean(message))}
            />
          ))
        : null}
      {product ? <InstructionLink product={product} /> : null}
      {variant === "draft" ? null : !orderComplete(value) ? (
        <p className="text-xs text-textSecondary">
          Läkemedel, dos och frekvens krävs; behandlingslängden är frivillig.
          Patienten ser ordinationen ordagrant som sitt recept i appen — den får
          inte gissas fram.
        </p>
      ) : doseError ? null : (
        // TES-702 — the patient's view, before publishing.
        //
        // The server composes the label from the three parts, and the doctor never
        // saw it before she pressed publish. Composition errors — the medication
        // written twice, a frequency that reads wrong together with the dose — can
        // only be seen this way, and only at the moment someone can change them.
        //
        // prescriptionLabel from the contract, not a string built here: if admin
        // shows something other than what the app renders, the preview is worse than
        // none at all.
        <p
          className="text-xs text-textSecondary"
          data-testid="order-patient-preview"
        >
          Patienten kommer att se:{" "}
          <span className="font-semibold text-textPrimary">
            {prescriptionLabel({
              medication: value.medication.trim(),
              dose: value.dose.trim(),
              frequency: value.frequency.trim(),
              duration: value.duration.trim() || null,
            })}
          </span>
        </p>
      )}
    </div>
  );
}

function productOptionLabel(p: MedicationCatalogueProduct): string {
  const label = catalogueProductLabel(p);
  return productInForce(p)
    ? label
    : `${label} – ${MEDICATION_CATALOGUE_COPY.notApproved}`;
}

// TES-1292 — the official instruction for the picked product, when its link
// is active, so the doctor sees what the patient will be sent to.
function InstructionLink({ product }: { product: MedicationCatalogueProduct }) {
  const url = product.current.instructionUrlActive
    ? product.current.instructionUrl
    : null;
  if (!url) return null;
  return (
    <p className="text-xs text-textSecondary">
      Officiell instruktion:{" "}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline"
      >
        {url}
      </a>
    </p>
  );
}
